import { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, Loader2, Share2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { WeekSelector } from '@/components/rsvp/WeekSelector';
import { useUIStore } from '@/stores/uiStore';
import { fetchMenus } from '@/lib/api/menus';
import {
  buildCookingDaySummaries,
  buildCookingHelperMessage,
  cookingHelperMessageDefaults,
} from '@/lib/utils/cookingHelperSchedule';
import { getCurrentWeekStart, getWeekRange, nextWeek, toISODate } from '@/lib/utils/dateHelpers';
import type { Menu } from '@/types/api';

export function CookingHelperSchedule() {
  const { showToast } = useUIStore();
  const [weekStart, setWeekStart] = useState(() => nextWeek(getCurrentWeekStart()));
  const [hoursPerMenuInput, setHoursPerMenuInput] = useState(String(cookingHelperMessageDefaults.hoursPerMenu));
  const [menus, setMenus] = useState<Menu[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCopying, setIsCopying] = useState(false);

  const { startDate, endDate } = useMemo(() => {
    const { end } = getWeekRange(weekStart);
    return {
      startDate: toISODate(weekStart),
      endDate: toISODate(end),
    };
  }, [weekStart]);

  const hoursPerMenu = (() => {
    const parsed = Number(hoursPerMenuInput);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : cookingHelperMessageDefaults.hoursPerMenu;
  })();

  const loadMenus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchMenus(startDate, endDate, 'PUBLISHED');
      setMenus(response.filter((menu) => menu.status === 'PUBLISHED'));
    } catch (error) {
      setMenus([]);
      showToast(error instanceof Error ? error.message : 'Failed to load menus', 'error');
    } finally {
      setLoading(false);
    }
  }, [endDate, showToast, startDate]);

  useEffect(() => {
    void loadMenus();
  }, [loadMenus]);

  const summaries = useMemo(() => buildCookingDaySummaries(menus, hoursPerMenu), [hoursPerMenu, menus]);

  const message = useMemo(
    () => buildCookingHelperMessage(summaries, {}),
    [summaries]
  );

  const handleCopy = async () => {
    if (!navigator.clipboard) {
      showToast('Clipboard is not available in this browser', 'error');
      return;
    }

    try {
      setIsCopying(true);
      await navigator.clipboard.writeText(message);
      showToast('Helper schedule copied', 'success');
    } catch {
      showToast('Failed to copy message', 'error');
    } finally {
      setIsCopying(false);
    }
  };

  const handleShare = async () => {
    if (!navigator.share) {
      showToast('Share is not supported on this device', 'error');
      return;
    }

    try {
      await navigator.share({
        title: 'Cooking Helper Schedule',
        text: message,
      });
    } catch {
      // no-op: user cancelled or share failed
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Cooking Helper Schedule</CardTitle>
          <CardDescription>
            Generate a ready-to-send helper message from next week&apos;s menus. Hours are calculated per cooking day.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <WeekSelector
            weekStart={weekStart}
            onWeekChange={setWeekStart}
            disabled={loading}
          />

          <div className="grid gap-3 sm:max-w-xs">
            <Label htmlFor="hours-per-menu">Hours per menu</Label>
            <Input
              id="hours-per-menu"
              inputMode="decimal"
              value={hoursPerMenuInput}
              onChange={(event) => setHoursPerMenuInput(event.target.value)}
            />
          </div>

          <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
            Pulling menus between <span className="font-medium text-foreground">{startDate}</span> and{' '}
            <span className="font-medium text-foreground">{endDate}</span>.
          </div>

          <div className="rounded-md border p-4 bg-background">
            <pre className="whitespace-pre-wrap text-sm leading-6 font-sans">{message}</pre>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={handleCopy} disabled={loading || isCopying} className="gap-2">
              {isCopying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
              Copy message
            </Button>
            {navigator.share && (
              <Button type="button" variant="outline" onClick={handleShare} disabled={loading} className="gap-2">
                <Share2 className="h-4 w-4" />
                Share
              </Button>
            )}
          </div>

          {loading && <p className="text-sm text-muted-foreground">Loading menus…</p>}
          {!loading && summaries.length === 0 && (
            <p className="text-sm text-muted-foreground">No cooking days found for this week.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

